import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

type Status = "idle" | "pending" | "running" | "done" | "unavailable" | "error";

interface FaceState {
  status: Status;
  url: string | null;
}

const INITIAL: FaceState = { status: "idle", url: null };

/**
 * Déclenche le rendu "image réelle" d'une carte (conteneur Shoggoth dockerisé,
 * exécuté à la demande via GitHub Actions, avec cache R2) et affiche l'état
 * d'avancement du recto ET du verso séparément : une carte peut n'avoir pas
 * de verso distinct (statut "unavailable", différent d'une erreur réelle).
 */
export function RenderPreview({ projectId, cardId, lang }: { projectId: string; cardId: string; lang: string | null }) {
  const [front, setFront] = useState<FaceState>(INITIAL);
  const [back, setBack] = useState<FaceState>(INITIAL);
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const startRender = useCallback(async () => {
    setFront({ status: "pending", url: null });
    setBack({ status: "pending", url: null });
    stopPolling();

    try {
      const result = await api.requestRender(projectId, { cardId, lang, format: "jpeg", bleed: 0 });
      setFront({
        status: result.front.status as Status,
        url: result.front.status === "done" ? result.front.url ?? null : null,
      });
      setBack({
        status: result.back.status as Status,
        url: result.back.status === "done" ? result.back.url ?? null : null,
      });

      const frontKey = result.front.status !== "done" ? result.front.cacheKey : null;
      const backKey = result.back.status !== "done" ? result.back.cacheKey : null;
      if (!frontKey && !backKey) return;

      pollRef.current = window.setInterval(async () => {
        try {
          if (frontKey) {
            const s = await api.renderStatus(frontKey);
            setFront({ status: s.status as Status, url: s.url ?? null });
          }
          if (backKey) {
            const s = await api.renderStatus(backKey);
            setBack({ status: s.status as Status, url: s.url ?? null });
          }
        } catch {
          setFront((f) => (f.status === "done" ? f : { status: "error", url: null }));
          setBack((b) => (b.status === "done" ? b : { status: "error", url: null }));
          stopPolling();
        }
      }, 4000);
    } catch {
      setFront({ status: "error", url: null });
      setBack({ status: "error", url: null });
    }
  }, [projectId, cardId, lang, stopPolling]);

  // Arrête le polling dès que les deux faces sont dans un état final.
  useEffect(() => {
    const isFinal = (s: Status) => s === "done" || s === "unavailable" || s === "error";
    if (isFinal(front.status) && isFinal(back.status)) stopPolling();
  }, [front.status, back.status, stopPolling]);

  function FacePreview({ label, state }: { label: string; state: FaceState }) {
    if (state.status === "idle" || state.status === "unavailable") return null;
    return (
      <div className="render-preview__face">
        <p className="render-preview__face-label">{label}</p>
        {(state.status === "pending" || state.status === "running") && (
          <p className="render-preview__status">Génération en cours…</p>
        )}
        {state.status === "error" && <p className="error">Échec du rendu.</p>}
        {state.status === "done" && state.url && (
          <img className="render-preview__image" src={state.url} alt={`${label} de la carte ${cardId}`} />
        )}
      </div>
    );
  }

  return (
    <div className="render-preview">
      {front.status === "idle" && (
        <button className="btn" onClick={startRender}>
          Voir le rendu final (image réelle)
        </button>
      )}
      {front.status !== "idle" && (
        <div className="render-preview__faces">
          <FacePreview label="Recto" state={front} />
          <FacePreview label="Verso" state={back} />
        </div>
      )}
      {front.status === "error" && (
        <button className="btn btn--ghost btn--small" onClick={startRender}>
          Réessayer
        </button>
      )}
    </div>
  );
}
